import '../../css/Settings.css';
import { useState, useEffect, useRef } from "react";
import {getGeneralSettings, updateGeneralSettings} from "../../service/api/orders";

export default function GeneralSettings() {
    const [loading, setLoading]   = useState(true);
    const [saving, setSaving]     = useState(false);
    const [saved, setSaved]       = useState(false);
    const [error, setError]       = useState(null);
    const savedTimer              = useRef(null);

    const [settings, setSettings] = useState({
        order_holding:               { active: false, distance: 0 },
        referral_delivery_incentive: { active: false, percentage: 0, active_days: 0 },
        fast_express:                { active: false },
    });

    // track unsaved changes
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        fetchGeneralSettings();
        return () => clearTimeout(savedTimer.current);
    }, []);

    const fetchGeneralSettings = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await getGeneralSettings();
            setSettings({
                order_holding:               res?.data?.order_holding               ?? { active: false, distance: 0 },
                referral_delivery_incentive: res?.data?.referral_delivery_incentive ?? { active: false, percentage: 0, active_days: 0 },
                fast_express:                res?.data?.fast_express               ?? { active: false },
            });
        } catch {
            setError('Failed to load settings.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = () => {
        setSettings(prev => ({
            ...prev,
            order_holding: {
                ...prev.order_holding,
                active: !prev.order_holding?.active,
            },
        }));
        setDirty(true);
    };

    const handleDistance = (e) => {
        const val = parseFloat(e.target.value) || 0;
        setSettings(prev => ({
            ...prev,
            order_holding: { ...prev.order_holding, distance: val },
        }));
        setDirty(true);
    };

    const handleReferralToggle = () => {
        setSettings(prev => ({
            ...prev,
            referral_delivery_incentive: {
                ...prev.referral_delivery_incentive,
                active: !prev.referral_delivery_incentive?.active,
            },
        }));
        setDirty(true);
    };

    const handleReferralPercentage = (e) => {
        const val = parseFloat(e.target.value) || 0;
        setSettings(prev => ({
            ...prev,
            referral_delivery_incentive: { ...prev.referral_delivery_incentive, percentage: val },
        }));
        setDirty(true);
    };


    const handleReferralActiveDays = (e) => {
        const val = parseInt(e.target.value) || 0;
        setSettings(prev => ({
            ...prev,
            referral_delivery_incentive: {
                ...prev.referral_delivery_incentive,
                active_days: val,
            },
        }));
        setDirty(true);
    };

    const handleFastExpressToggle = () => {
        setSettings(prev => ({
            ...prev,
            fast_express: {
                ...prev.fast_express,
                active: !prev.fast_express?.active,
            },
        }));
        setDirty(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const res = await updateGeneralSettings(settings); // pass whole object now
            if (!res?.success) throw new Error('Server error');
            setDirty(false);
            setSaved(true);
            savedTimer.current = setTimeout(() => setSaved(false), 2500);
        } catch {
            setError('Failed to save settings. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const oh = settings.order_holding;
    const ri = settings.referral_delivery_incentive;
    const fe = settings.fast_express;

    return (
        <div className="gs-root">
            {/* Header */}
            <div className="gs-header">
                <div className="gs-header-left">
                    <h2 className="gs-title">General Settings</h2>
                    <p className="gs-subtitle">Configure platform-wide operational behaviour</p>
                </div>
                <button
                    className={`gs-save-btn ${saving ? 'gs-save-btn--loading' : ''} ${saved ? 'gs-save-btn--saved' : ''}`}
                    onClick={handleSave}
                    disabled={saving || !dirty}
                >
                    {saving ? (
                        <>
                            <svg className="gs-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                            </svg>
                            Saving…
                        </>
                    ) : saved ? (
                        <>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                                <polyline points="20 6 9 17 4 12"/>
                            </svg>
                            Saved
                        </>
                    ) : (
                        <>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
                            </svg>
                            Save Changes
                        </>
                    )}
                </button>
            </div>

            {error && (
                <div className="gs-error-banner">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    {error}
                </div>
            )}

            {loading ? (
                <div className="gs-skeleton-wrap">
                    <div className="gs-skeleton" />
                    <div className="gs-skeleton gs-skeleton--short" />
                </div>
            ) : (
                <div className="gs-sections">

                    {/* Fast Express Card */}
                    <div className="gs-card">
                        <div className="gs-card-header">
                            <div className="gs-card-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>
                                </svg>
                            </div>
                            <div>
                                <div className="gs-card-title">Fast Express</div>
                                <div className="gs-card-desc">Enable expedited delivery mode platform-wide</div>
                            </div>
                        </div>

                        <div className="gs-card-body">
                            <div className="gs-row">
                                <div className="gs-row-label">
                                    <span>Enable Fast Express</span>
                                    <span className={`gs-badge ${fe.active ? 'gs-badge--on' : 'gs-badge--off'}`}>
                                        {fe.active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <button
                                    className={`gs-toggle ${fe.active ? 'gs-toggle--on' : ''}`}
                                    onClick={handleFastExpressToggle}
                                    aria-label="Toggle fast express"
                                >
                                    <span className="gs-toggle-thumb"/>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="gs-card">
                        <div className="gs-card-header">
                            <div className="gs-card-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                    <circle cx="9" cy="7" r="4"/>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                </svg>
                            </div>
                            <div>
                                <div className="gs-card-title">Referral Delivery Incentive</div>
                                <div className="gs-card-desc">Give delivery partners a percentage bonus on earnings for
                                    orders from referred customers
                                </div>
                            </div>
                        </div>

                        <div className="gs-card-body">
                            {/* Toggle Row */}
                            <div className="gs-row">
                                <div className="gs-row-label">
                                    <span>Enable Referral Incentive</span>
                                    <span className={`gs-badge ${ri.active ? 'gs-badge--on' : 'gs-badge--off'}`}>
                        {ri.active ? 'Active' : 'Inactive'}
                    </span>
                                </div>
                                <button
                                    className={`gs-toggle ${ri.active ? 'gs-toggle--on' : ''}`}
                                    onClick={handleReferralToggle}
                                    aria-label="Toggle referral incentive"
                                >
                                    <span className="gs-toggle-thumb"/>
                                </button>
                            </div>

                            <div className="gs-divider"/>

                            {/* Percentage Row */}
                            {/* Incentive Row — percentage + active days inline */}
                            <div className={`gs-row gs-row--distance ${!ri.active ? 'gs-row--disabled' : ''}`}>
                                <div className="gs-row-label">
                                    <span>Incentive Rules</span>
                                    <span className="gs-row-hint">Bonus % on earnings · Min active days to qualify</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div className="gs-distance-input-wrap">
                                        <input
                                            type="number"
                                            className="gs-distance-input"
                                            value={ri.percentage}
                                            onChange={handleReferralPercentage}
                                            min={0}
                                            max={100}
                                            step={0.5}
                                            disabled={!ri.active}
                                        />
                                        <span className="gs-distance-unit">%</span>
                                    </div>
                                    <span style={{ color: '#cbd5e1', fontSize: 16, fontWeight: 300 }}>·</span>
                                    <div className="gs-distance-input-wrap">
                                        <input
                                            type="number"
                                            className="gs-distance-input"
                                            value={ri.active_days ?? 0}
                                            onChange={handleReferralActiveDays}
                                            min={0}
                                            step={1}
                                            disabled={!ri.active}
                                        />
                                        <span className="gs-distance-unit">days</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Order Holding Card */}
                    <div className="gs-card">
                        <div className="gs-card-header">
                            <div className="gs-card-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"/>
                                    <polyline points="12 6 12 12 16 14"/>
                                </svg>
                            </div>
                            <div>
                                <div className="gs-card-title">Order Holding System</div>
                                <div className="gs-card-desc">Hold incoming orders when delivery partners are beyond
                                    threshold distance
                                </div>
                            </div>
                        </div>

                        <div className="gs-card-body">
                            {/* Toggle Row */}
                            <div className="gs-row">
                                <div className="gs-row-label">
                                    <span>Enable Order Holding</span>
                                    <span className={`gs-badge ${oh.active ? 'gs-badge--on' : 'gs-badge--off'}`}>
                                        {oh.active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <button
                                    className={`gs-toggle ${oh.active ? 'gs-toggle--on' : ''}`}
                                    onClick={handleToggle}
                                    aria-label="Toggle order holding"
                                >
                                    <span className="gs-toggle-thumb"/>
                                </button>
                            </div>

                            <div className="gs-divider"/>

                            {/* Distance Row */}
                            <div className={`gs-row gs-row--distance ${!oh.active ? 'gs-row--disabled' : ''}`}>
                                <div className="gs-row-label">
                                    <span>Hold Distance Threshold</span>
                                    <span
                                        className="gs-row-hint">Orders held when nearest partner exceeds this distance</span>
                                </div>
                                <div className="gs-distance-input-wrap">
                                    <input
                                        type="number"
                                        className="gs-distance-input"
                                        value={oh.distance}
                                        onChange={handleDistance}
                                        min={0}
                                        step={0.5}
                                        disabled={!oh.active}
                                    />
                                    <span className="gs-distance-unit">km</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}